import * as React from 'react';
import { IndexRoute, Route, Router } from 'react-router';
import App from './containers/App/App';

import UrlMapper from './containers/UrlMapper/UrlMapper';
import Login from './containers/Login/Login';
import Sets from './containers/Sets/Sets';
import './theme/globalStyles';
import Sitemap from './containers/Sitemap/Sitemap';
import NotIncludedSchoolsFrom from './containers/NotIncludedSchoolsFrom/NotIncludedSchoolsFrom';
import {
  URL_ACCOUNT_BULK_ORDER,
  URL_ACCOUNT_PRODUCTS_FOR_SETS,
  URL_ACCOUNT_SETS,
  URL_SCHOOL_ARTICLES,
  URL_FOR_SCHOOLS,
  URL_FOR_ORGANIZATIONS,
  URL_CREDIT,
  URL_LOGIN,
  URL_REGISTRATION_COMPANY,
  URL_REGISTRATION_COMPLETE_COMPANY,
  URL_REGISTRATION_COMPLETE_RETAIL,
  URL_REGISTRATION_COMPLETE_SCHOOL,
  URL_REGISTRATION_RETAIL,
  URL_REGISTRATION_SCHOOL,
  URL_REQUEST_PRODUCT_PRICE,
  URL_SEARCH,
  URL_SET,
  URL_SET_EDIT_PRODUCTS,
  URL_SET_NEW,
  URL_SET_NEW_CHOOSE,
  URL_SETS,
  URL_SKVELKOBOX_BENEFITS,
  URL_SKVELKOBOX_INSTRUCTIONS,
  URL_VIDEO_TUTORIALS,
  URL_REGISTRATION_HUB,
  URL_WITHDRAWAL_FORM_THANK_YOU,
} from './constants';
import RegistrationParent from './containers/Registration/RegistrationParent';
import RegistrationSchool from './components/Registration/RegistrationSchool';
import RegistrationCompany from './components/Registration/RegistrationCompany';
import RegistrationRetail from './components/Registration/RegistrationRetail';
import SkvelkoBoxParent from './containers/Ucet/SkvelkoBoxParent';
import Search from './containers/Search/Search';
import SchoolArticles from './containers/SchoolArticle/SchoolArticles';
import { OrganizationsIntro } from './containers/AudienceIntro/OrganizationsIntro/OrganizationsIntro';
import { SchoolsIntro } from './containers/AudienceIntro/SchoolsIntro/SchoolsIntro';
import { SelfIntro } from './containers/AudienceIntro/SelfIntro/SelfIntro';
import ClearReduxCache from './containers/System/ClearReduxCache';
import loadable from '@loadable/component';
import { scrollToTop } from './utilities';
import LoadingFallback from './components/_helpers/LoadingFallback/LoadingFallback';
import RegistrationHub from './components/Registration/RegistrationHub';

const withSuspense = (Component) => (props) => (
  <React.Suspense fallback={<LoadingFallback />}>
    <Component {...props} />
  </React.Suspense>
);

const thisScrollToTop = (props) => {
  if (typeof window !== 'undefined' && sessionStorage) {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'; // disable auto scroll
    }

    const currentLocation = props.history.getCurrentLocation();

    let locationHistoryRaw = sessionStorage.getItem('locationHistory');
    if (!locationHistoryRaw) {
      locationHistoryRaw = `{"locationHistories":[]}`;
    }

    const locationHistory: {
      locationHistories: { pathName: string; scrollY: number }[];
    } = JSON.parse(locationHistoryRaw);

    if (locationHistory.locationHistories.length > 1) {
      if (
        locationHistory.locationHistories[
          locationHistory.locationHistories.length - 2
        ].pathName === currentLocation.pathname
      ) {
        const lastLocationHistory = locationHistory.locationHistories.pop();
        window.addEventListener(
          'scrollToTop',
          (e) => {
            if (!window.location.pathname.startsWith(URL_SET)) {
              window.scrollTo({
                top: lastLocationHistory?.scrollY,
                left: 0,
                behavior: 'smooth',
              });
            }
          },
          { once: true },
        );
      } else {
        locationHistory.locationHistories.push({
          pathName: currentLocation.pathname,
          scrollY: window.scrollY,
        });
        window.addEventListener(
          'scrollToTop',
          (e) => {
            if (!window.location.pathname.startsWith(URL_SET)) {
              scrollToTop();
            }
          },
          { once: true },
        );
      }
    } else {
      locationHistory.locationHistories.push({
        pathName: currentLocation.pathname,
        scrollY: window.scrollY,
      });
    }
    sessionStorage.setItem('locationHistory', JSON.stringify(locationHistory));
  } else {
    if (!window.location.pathname.startsWith(URL_SET)) {
      scrollToTop();
    }
  }
};

const Routes = (props: any) => {
  return (
    <Router onUpdate={() => thisScrollToTop(props)} history={props.history}>
      <Route path="/" component={App}>
        <Route
          component={withSuspense(
            loadable(() => import('./containers/Ucet/Ucet')),
          )}
        >
          <Route
            path="/my-account"
            component={withSuspense(
              loadable(() => import('./containers/Ucet/MojUcet')),
            )}
          />
          <Route
            component={withSuspense(
              loadable(() => import('./containers/Ucet/SetsParent')),
            )}
          >
            <Route
              path={URL_ACCOUNT_SETS}
              component={withSuspense(
                loadable(() => import('./containers/Ucet/SetsManagement')),
              )}
            />
            <Route
              path={URL_ACCOUNT_PRODUCTS_FOR_SETS}
              component={withSuspense(
                loadable(() => import('./containers/Ucet/ProductsForSets')),
              )}
            />
            <Route
              path={URL_ACCOUNT_BULK_ORDER}
              component={withSuspense(
                loadable(() => import('./containers/Ucet/SetBulkOrder')),
              )}
            />
          </Route>
          <Route
            path={URL_SET_NEW}
            component={withSuspense(
              loadable(() => import('./containers/Ucet/NewSet')),
            )}
          />
          <Route
            path="/my-account/set/edit/:id"
            component={withSuspense(
              loadable(() => import('./containers/Ucet/NewSet')),
            )}
          />

          <Route
            path="/my-account/classrooms"
            component={withSuspense(
              loadable(() => import('./containers/Ucet/ClassroomList')),
            )}
          />

          <Route
            path="/my-account/renumeration"
            component={withSuspense(
              loadable(() => import('./containers/Ucet/RemunerationSystem')),
            )}
          />

          <Route
            path="/my-account/renumeration/awarded"
            component={withSuspense(
              loadable(
                () => import('./containers/Ucet/RemunerationSystemAwarded'),
              ),
            )}
          />

          <Route
            path="/my-account/renumeration/new-user"
            component={withSuspense(
              loadable(() => import('./containers/Ucet/NewSkvelkoAwardedUser')),
            )}
          />

          <Route
            path={URL_CREDIT}
            component={withSuspense(
              loadable(() => import('./containers/Ucet/Credit')),
            )}
          />

          <Route
            path="/my-account/my-orders"
            component={withSuspense(
              loadable(() => import('./containers/Ucet/OrderHistory')),
            )}
          />
          <Route
            path="/my-account/favorite-products"
            component={withSuspense(
              loadable(() => import('./containers/Ucet/FavoriteProducts')),
            )}
          />

          <Route
            path="/my-account/most-frequently-purchased-products"
            component={withSuspense(
              loadable(
                () =>
                  import('./containers/Ucet/MostFrequentlyPurchasedProducts'),
              ),
            )}
          />

          <Route
            path="/my-account/order/:orderPublicId"
            component={withSuspense(
              loadable(() => import('./containers/Ucet/OrderDetail')),
            )}
          />

          <Route
            path="/my-account/settings"
            component={withSuspense(
              loadable(() => import('./containers/Ucet/Settings')),
            )}
          />

          <Route component={SkvelkoBoxParent}>
            <Route
              path={URL_SKVELKOBOX_BENEFITS}
              component={withSuspense(
                loadable(() => import('./containers/Ucet/SkvelkoBoxBenefits')),
              )}
            />
            <Route
              path={URL_SKVELKOBOX_INSTRUCTIONS}
              component={withSuspense(
                loadable(
                  () => import('./containers/Ucet/SkvelkoBoxInstructions'),
                ),
              )}
            />
          </Route>

          <Route
            path="/my-account/products-customer"
            component={withSuspense(
              loadable(() => import('./containers/Ucet/ProductsCustomer')),
            )}
          />

          <Route
            path={URL_VIDEO_TUTORIALS}
            component={withSuspense(
              loadable(() => import('./containers/Ucet/VideoTutorials')),
            )}
          />
        </Route>

        <Route
          path="zabudnute-heslo"
          component={withSuspense(
            loadable(() => import('./containers/Login/ForgottenPassword')),
          )}
        />
        <Route
          path="zabudnute-heslo-firma"
          component={withSuspense(
            loadable(
              () => import('./containers/Login/ForgottenPasswordCompany'),
            ),
          )}
        />

        <Route
          path={URL_SET_NEW_CHOOSE}
          component={withSuspense(
            loadable(() => import('./containers/Ucet/NewSetChoose')),
          )}
        />

        <Route path={URL_REGISTRATION_HUB} component={RegistrationHub} />

        <Route component={RegistrationParent}>
          <Route
            path={URL_REGISTRATION_RETAIL}
            component={RegistrationRetail}
          />
          <Route
            path={URL_REGISTRATION_COMPANY}
            component={RegistrationCompany}
          />
          <Route
            path={URL_REGISTRATION_SCHOOL}
            component={RegistrationSchool}
          />
        </Route>

        <Route
          path={URL_REGISTRATION_COMPLETE_RETAIL}
          component={withSuspense(
            loadable(() => import('./containers/Registration/FinishedRetail')),
          )}
        />
        <Route
          path={URL_REGISTRATION_COMPLETE_COMPANY}
          component={withSuspense(
            loadable(() => import('./containers/Registration/FinishedCompany')),
          )}
        />
        <Route
          path={URL_REGISTRATION_COMPLETE_SCHOOL}
          component={withSuspense(
            loadable(() => import('./containers/Registration/FinishedSchool')),
          )}
        />

        <Route
          path={URL_REQUEST_PRODUCT_PRICE}
          component={withSuspense(
            loadable(() => import('./containers/Ucet/RequestProductPrice')),
          )}
        />

        <Route
          path={URL_SET_EDIT_PRODUCTS}
          component={withSuspense(
            loadable(() => import('./containers/Ucet/SetProducts')),
          )}
        />

        <Route
          component={withSuspense(
            loadable(() => import('./containers/Cart/Cart')),
          )}
        >
          <Route
            path={'/kosik'}
            component={withSuspense(
              loadable(() => import('./components/Cart/Cart1')),
            )}
          />
          <Route
            path={'/doprava-a-platba'}
            component={withSuspense(
              loadable(() => import('./components/Cart/Cart2')),
            )}
          />
          <Route
            path={'/udaje-pre-dorucenie'}
            component={withSuspense(
              loadable(() => import('./components/Cart/Cart3')),
            )}
          />
        </Route>

        <Route
          path={'/objednavka-dokoncena/:orderPublicId'}
          component={withSuspense(
            loadable(() => import('./containers/Cart/FinishOrder')),
          )}
        />

        <Route path={URL_LOGIN} component={Login} />
        <Route
          path="/logout"
          component={withSuspense(
            loadable(() => import('./containers/Logout/Logout')),
          )}
        />

        <Route
          path="nove-heslo/:token"
          component={withSuspense(
            loadable(() => import('./containers/Login/RenewPassword')),
          )}
        />
        <Route
          path="activation/:code"
          component={withSuspense(
            loadable(() => import('./containers/Activation/Activation')),
          )}
        />

        <Route path={URL_SETS} component={Sets} />
        <Route path={`${URL_SET}/:id`} component={Sets} />

        <Route path="mapa-stranok" component={Sitemap} />

        <Route
          path="formular-pre-nezaradene-skoly"
          component={NotIncludedSchoolsFrom}
        />

        {/*}
        <Route
          path={URL_WITHDRAWAL_FORM}
          component={withSuspense(
            loadable(
              () => import('./containers/WithdrawalForm/WithdrawalForm'),
            ),
          )}
        />
        */}

        <Route
          path={URL_WITHDRAWAL_FORM_THANK_YOU}
          component={withSuspense(
            loadable(
              () =>
                import('./containers/WithdrawalForm/WithdrawalFormThankYou'),
            ),
          )}
        />

        <Route path={URL_SEARCH} component={Search} />
        <Route path={URL_SCHOOL_ARTICLES} component={SchoolArticles} />

        {/* Audience-tab intro ("Úvod") landings — fixed frontend routes. */}
        <Route path={URL_FOR_SCHOOLS} component={SchoolsIntro} />
        <Route path={URL_FOR_ORGANIZATIONS} component={OrganizationsIntro} />

        <Route
          path="survey/:orderPublicId"
          component={withSuspense(
            loadable(() => import('./containers/Survey/Survey')),
          )}
        />

        <Route
          path="/NGdwmCpqsgmkAZ4t"
          component={withSuspense(
            loadable(() => import('./containers/Errors/ErrorCaller')),
          )}
        />
        <Route path="/clear-redux-cache" component={ClearReduxCache} />

        <Route
          path="/unsubscribe/:userHash"
          component={withSuspense(
            loadable(() => import('./containers/Unsubscribe/Unsubscribe')),
          )}
        />

        <Route path="**" component={UrlMapper} />
        <IndexRoute component={SelfIntro} />
      </Route>
    </Router>
  );
};

export default Routes;
